import type {
  useAssignEntity,
  useRemoveAssignment,
  useUpdateDeduplicationDecision,
} from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import {
  useBulkDeduplicationAction,
  usePagedDeduplicationResults,
} from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import type { DeduplicationResultWithRecord } from '@app/src/types/tabularImport'
import {
  DeduplicationMatchSignal,
  DeduplicationUserAction,
  parseExtractedData,
  parseFieldDecisions,
  parseSnapshot,
} from '@app/src/types/tabularImport'
import { ChevronRight, ExpandMoreOutlined } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import type { EntitySearchAssignProps } from './EntitySearchAssignProps'
import FieldCompareView from './FieldCompareView'

interface DeduplicationSectionProps {
  sessionId: string
  title: string
  description: string
  color: 'success' | 'info' | 'warning' | 'error' | 'default'
  count: number
  action: DeduplicationUserAction
  expanded: boolean
  onToggle: () => void
  updateDecision: ReturnType<typeof useUpdateDeduplicationDecision>
  assignEntity: ReturnType<typeof useAssignEntity>
  removeAssignment: ReturnType<typeof useRemoveAssignment>
  assignSearch: (props: EntitySearchAssignProps) => React.ReactNode
}

// Label shown for each match signal + the extracted field(s) whose value(s) triggered it.
const SIGNAL_INFO: Record<DeduplicationMatchSignal, { label: string; fieldKeys: string[] }> = {
  [DeduplicationMatchSignal.None]: { label: '—', fieldKeys: [] },
  [DeduplicationMatchSignal.WorldfavorId]: { label: 'WF ID', fieldKeys: ['WorldfavorId'] },
  [DeduplicationMatchSignal.CustomId]: { label: 'Custom ID', fieldKeys: ['CustomId', 'ProductCustomId'] },
  [DeduplicationMatchSignal.RegistrationNumber]: { label: 'Reg. No.', fieldKeys: ['RegistrationNumber'] },
  [DeduplicationMatchSignal.Duns]: { label: 'DUNS', fieldKeys: ['Duns'] },
  [DeduplicationMatchSignal.NameCountry]: { label: 'Name + Country', fieldKeys: ['Name', 'Country'] },
  [DeduplicationMatchSignal.Gtin]: { label: 'GTIN', fieldKeys: ['Gtin'] },
}

type BulkAction = { label: string; targetAction: DeduplicationUserAction }

const bulkActionsBySection: Partial<Record<DeduplicationUserAction, BulkAction[]>> = {
  [DeduplicationUserAction.Pending]: [
    { label: 'Accept All', targetAction: DeduplicationUserAction.Update },
    { label: 'All New', targetAction: DeduplicationUserAction.CreateNew },
    { label: 'Skip All', targetAction: DeduplicationUserAction.Skip },
  ],
  [DeduplicationUserAction.Update]: [
    { label: 'All New', targetAction: DeduplicationUserAction.CreateNew },
    { label: 'Skip All', targetAction: DeduplicationUserAction.Skip },
  ],
  [DeduplicationUserAction.CreateNew]: [{ label: 'Skip All', targetAction: DeduplicationUserAction.Skip }],
}

function getBulkActionsForSection(action: DeduplicationUserAction): BulkAction[] {
  return bulkActionsBySection[action] ?? []
}

const PAGE_SIZE = 50

const DeduplicationSection: React.FC<DeduplicationSectionProps> = ({
  sessionId,
  title,
  description,
  color,
  count,
  action,
  expanded,
  onToggle,
  updateDecision,
  assignEntity,
  removeAssignment,
  assignSearch,
}) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [expandedCompareId, setExpandedCompareId] = useState<number | null>(null)

  // Only fetch when section is expanded
  const { data, isLoading } = usePagedDeduplicationResults(
    sessionId,
    { action, page, pageSize: PAGE_SIZE, search: search.length >= 2 ? search : undefined },
    expanded,
  )

  const bulkAction = useBulkDeduplicationAction(sessionId)

  const results: DeduplicationResultWithRecord[] = (data?.items ?? []).map(d => ({
    ...d,
    importRecord: d.record ?? {
      id: d.tabularImportRecordId,
      rowNumber: 0,
      status: 'Success',
      extractedData: null,
      validationErrors: [],
      processedAt: null,
    },
  }))

  const bulkActions = getBulkActionsForSection(action)
  const autoExpandCompare = action === DeduplicationUserAction.Pending

  return (
    <Accordion
      elevation={0}
      disableGutters
      expanded={expanded}
      onChange={onToggle}
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
        <Box display="flex" alignItems="center" gap={1.5} width="100%">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: color === 'default' ? 'text.disabled' : `${color}.main`,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <Chip
            label={`${count.toLocaleString()} rows`}
            size="small"
            color={color === 'default' ? undefined : color}
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {/* Toolbar: search + bulk actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pb={1} gap={2}>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              setPage(1)
            }}
            sx={{ maxWidth: 300 }}
          />
          <Box display="flex" gap={1}>
            {bulkActions.map(ba => (
              <Button
                key={ba.label}
                size="small"
                variant="outlined"
                onClick={() =>
                  bulkAction.mutate({
                    currentAction: action,
                    newAction: ba.targetAction,
                    ids: null,
                  })
                }
                disabled={bulkAction.isPending}
              >
                {ba.label}
              </Button>
            ))}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mx: 2, mb: 2, width: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Import Name</TableCell>
                  <TableCell>Match Signal</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Matched</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Assign</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map(result => {
                  const extracted = parseExtractedData(result.importRecord.extractedData)
                  const importName = extracted?.extractedFields?.Name ?? '—'
                  const matchedValue =
                    SIGNAL_INFO[result.matchSignal].fieldKeys
                      .map(key => extracted?.extractedFields?.[key])
                      .filter(Boolean)
                      .join(' · ') || null
                  const hasOriginalMatch = Boolean(result.matchedEntityId)
                  const hasAssignment = Boolean(result.assignedEntityId)
                  const hasEntity = hasOriginalMatch || hasAssignment
                  const originalSnapshot = hasOriginalMatch ? parseSnapshot(result.matchedEntitySnapshot) : null
                  const assignedSnapshot = hasAssignment ? parseSnapshot(result.assignedEntitySnapshot) : null
                  const effectiveSnapshot = assignedSnapshot ?? originalSnapshot
                  const matchedName = originalSnapshot?.Name ?? (hasOriginalMatch ? '—' : '')
                  const isExpanded = expandedCompareId === result.id

                  return (
                    <React.Fragment key={result.id}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2">{result.importRecord.rowNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{importName}</Typography>
                        </TableCell>
                        <TableCell>
                          {hasOriginalMatch ? (
                            <Box>
                              <Chip label={SIGNAL_INFO[result.matchSignal].label} size="small" variant="outlined" />
                              {matchedValue && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {matchedValue}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {hasOriginalMatch ? `${result.confidenceScore}%` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{matchedName || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={result.userAction}
                            onChange={(_, value) => {
                              if (value === null) return
                              updateDecision.mutate({ id: result.id, action: value })
                            }}
                          >
                            {hasEntity && <ToggleButton value={DeduplicationUserAction.Update}>Update</ToggleButton>}
                            <ToggleButton value={DeduplicationUserAction.CreateNew}>New</ToggleButton>
                            <ToggleButton value={DeduplicationUserAction.Skip}>Skip</ToggleButton>
                          </ToggleButtonGroup>
                        </TableCell>
                        <TableCell>
                          {assignSearch({
                            assignedEntityId: result.assignedEntityId,
                            assignedEntityName: assignedSnapshot?.Name,
                            onAssign: entityId => assignEntity.mutate({ id: result.id, entityId }),
                            onRemove: () => removeAssignment.mutate(result.id),
                          })}
                        </TableCell>
                        <TableCell>
                          {hasEntity && effectiveSnapshot && !autoExpandCompare && (
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<ChevronRight />}
                              onClick={() => setExpandedCompareId(prev => (prev === result.id ? null : result.id))}
                            >
                              Compare fields
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {hasEntity && effectiveSnapshot && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                            <Collapse in={autoExpandCompare || isExpanded} unmountOnExit>
                              <Box sx={{ p: 1.5 }}>
                                <FieldCompareView
                                  importRecord={result.importRecord}
                                  matchedEntitySnapshot={effectiveSnapshot}
                                  fieldDecisions={parseFieldDecisions(result.fieldDecisions)}
                                  onSave={fieldDecisions => {
                                    updateDecision.mutate({
                                      id: result.id,
                                      action: DeduplicationUserAction.Update,
                                      fieldDecisions,
                                    })
                                    setExpandedCompareId(null)
                                  }}
                                  onClose={() => setExpandedCompareId(null)}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={data?.totalCount ?? 0}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default DeduplicationSection
